// Loads the brand kit's font files for the content preview. Fonts load in CORS mode and the shared
// Mech Storage bucket has no CORS for the web app, so @font-face URLs to storage fail. Instead each
// file is fetched from the API (session cookie, CORS with credentials) and registered with the
// FontFace API under its scoped alias (`hc-kit-heading`, ...). Faces are removed on unmount or when
// the kit (paths carry the kit version) changes. Failures leave the fallback stacks in place.
import { useEffect, useState } from "react";
import type { VisualKitFontFile } from "../../../../shared/visuals";
import { api } from "../../lib/api";
import { kitFontAlias } from "./kit-style";

export type FetchFontBytes = (path: string, signal: AbortSignal) => Promise<ArrayBuffer>;

interface FontEnv {
  FontFace: typeof FontFace;
  fonts: FontFaceSet;
}

function fontEnv(): FontEnv | null {
  const ctor = (globalThis as { FontFace?: typeof FontFace }).FontFace;
  const fonts = typeof document !== "undefined" ? (document as Document & { fonts?: FontFaceSet }).fonts : undefined;
  return ctor && fonts ? { FontFace: ctor, fonts } : null;
}

/** API-served font paths only (never an absolute URL from the response). */
function isApiPath(path: string): boolean {
  return path.startsWith("/api/") && !path.startsWith("//");
}

/**
 * Fetch and register every file; returns the faces it added. Each file is independent: one failure
 * does not stop the others. Nothing is added once `signal` is aborted.
 */
export async function loadKitFonts(
  files: readonly VisualKitFontFile[],
  fetchBytes: FetchFontBytes,
  signal: AbortSignal,
  env: FontEnv | null = fontEnv(),
): Promise<FontFace[]> {
  if (!env) return [];
  const added: FontFace[] = [];
  await Promise.all(files.filter((file) => isApiPath(file.path)).map(async (file) => {
    try {
      const bytes = await fetchBytes(file.path, signal);
      const face = new env.FontFace(kitFontAlias(file.role), bytes, {
        weight: String(Number.isInteger(file.weight) ? file.weight : 400),
        style: file.style === "italic" ? "italic" : "normal",
        display: "swap",
      });
      await face.load();
      if (signal.aborted) return;
      env.fonts.add(face);
      added.push(face);
    } catch (error) {
      if (!signal.aborted) console.warn("[preview] kit font failed to load; using fallback", { role: file.role, error: error instanceof Error ? error.message : String(error) });
    }
  }));
  return added;
}

const defaultFetch: FetchFontBytes = (path, signal) => api.getBytes(path, signal);

/** Registers the kit fonts while mounted. Returns how many faces are active. */
export function useKitFonts(files: readonly VisualKitFontFile[], fetchBytes: FetchFontBytes = defaultFetch): number {
  const [active, setActive] = useState(0);
  const key = files.map((file) => `${file.role}:${file.style}:${file.weight}:${file.path}`).join("|");
  useEffect(() => {
    const env = fontEnv();
    if (!env || !files.length) { setActive(0); return; }
    const controller = new AbortController();
    let added: FontFace[] = [];
    void loadKitFonts(files, fetchBytes, controller.signal, env).then((faces) => {
      if (controller.signal.aborted) { for (const face of faces) env.fonts.delete(face); return; }
      added = faces;
      setActive(faces.length);
    });
    return () => {
      controller.abort();
      for (const face of added) env.fonts.delete(face);
      added = [];
    };
    // `key` captures every field of `files` that matters; fetchBytes is stable in practice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return active;
}
